import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const placeholderReviews = [
  { initial: 'M', name: '', rating: 5, time: 'vorige maand', text: 'Zeer professioneel team! Onze loopband werd vakkundig gedemonteerd, verhuisd en weer opgebouwd. Alles werkt perfect.' },
  { initial: 'R', name: '', rating: 5, time: '2 maanden geleden', text: 'Snelle service en duidelijke communicatie. De monteurs wisten precies wat ze deden. Aanrader!' },
  { initial: 'J', name: '', rating: 5, time: '3 maanden geleden', text: 'Complete home gym laten installeren. Netjes gewerkt, alles schoon achtergelaten. Top service!' },
  { initial: 'S', name: '', rating: 5, time: '3 maanden geleden', text: 'Denken goed mee en gaan zorgvuldig om met de apparatuur. Van offerte tot uitvoering perfect geregeld.' },
  { initial: 'B', name: '', rating: 5, time: '4 maanden geleden', text: 'Onze bedrijfsfitness werd binnen één dag volledig verhuisd naar het nieuwe pand. Uitstekend werk!' },
  { initial: 'T', name: '', rating: 5, time: '5 maanden geleden', text: 'Betrouwbaar en vakkundig. Precies op tijd en tegen een eerlijke prijs.' }
];

export const GoogleReviews = () => {
  const [reviews, setReviews] = useState(placeholderReviews);
  const [avg, setAvg] = useState('5.0');

  useEffect(() => {
    axios.get(`${API}/reviews`).then((res) => {
      const real = res.data || [];
      if (real.length >= 3) {
        setReviews(real.slice(0, 6).map((r) => ({
          initial: (r.naam || '?')[0].toUpperCase(),
          name: r.naam + (r.plaats ? `, ${r.plaats}` : ''),
          rating: r.rating,
          time: new Date(r.created_at).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' }),
          text: r.tekst,
        })));
        setAvg((real.reduce((a, r) => a + r.rating, 0) / real.length).toFixed(1));
      }
    }).catch(() => {});
  }, []);

  return (
    <section className="py-16 lg:py-24 bg-white relative overflow-hidden" data-testid="google-reviews-section">
      <div className="container mx-auto px-4">
        {/* Header with rating */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 max-w-5xl mx-auto mb-14">
          <div className="flex items-center gap-5">
            {/* Google G logo */}
            <div className="w-16 h-16 bg-white rounded-2xl shadow-lg border border-neutral-100 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-9 h-9" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
            </div>
            <div>
              <div className="flex items-center gap-3">
                <span className="text-4xl font-black text-neutral-900">{avg}</span>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
              </div>
              <p className="text-neutral-500 text-sm mt-1">Geverifieerde Google Reviews</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://search.google.com/local/writereview?placeid=ChIJZxWp8PA3YkwRl27RH85P-fs"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-neutral-800 text-white px-6 py-3.5 rounded-2xl font-semibold transition-all duration-300 hover:scale-[1.02]"
              data-testid="write-review-button"
            >
              Review schrijven op Google
            </a>
            <a
              href="/reviews"
              className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black px-6 py-3.5 rounded-2xl font-semibold transition-all duration-300 hover:scale-[1.02]"
              data-testid="all-reviews-link"
            >
              Alle reviews bekijken
            </a>
          </div>
        </div>

        {/* Review cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {reviews.map((review, idx) => (
            <div
              key={idx}
              className="bg-neutral-50 border border-neutral-100 rounded-3xl p-6 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
              data-testid={`review-card-${idx}`}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 bg-yellow-400 rounded-full flex items-center justify-center text-black font-black text-lg">
                  {review.initial}
                </div>
                <div>
                  {review.name && <p className="text-neutral-900 text-sm font-bold leading-tight">{review.name}</p>}
                  <div className="flex gap-0.5">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-neutral-400 text-xs mt-0.5">{review.time}</p>
                </div>
              </div>
              <p className="text-neutral-700 leading-relaxed text-sm">"{review.text}"</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
